import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Paper,
  IconButton,
  Tooltip,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import LoadingError from "./LoadingError";
import {
  formatColumnName,
  formatCellValue,
  getColumnTooltip,
} from "../utils/columnConfig";

const ReportsTableView = ({
  data,
  loading,
  error,
  visibleColumns,
  columnOrder,
  sortConfig,
  onSort,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onRowClick,
  userManufacturer,
}) => {
  // 1. Ensure Loading/Error state occupies reserved space (CLS Fix)
  if (loading || error) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "1px solid #e0e0e0",
          borderRadius: 1,
          minHeight: "400px", // Reserve minimum vertical space
        }}
      >
        <LoadingError isLoading={loading} error={error} />
      </Box>
    );
  }

  const paginatedData = data.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box
      sx={{
        flex: 1, // Takes up remaining vertical space in parent flex column
        display: "flex",
        flexDirection: "column",
        minWidth: 0, // Prevents Box from stretching horizontally out of bounds
        overflow: "auto", // Allows vertical scroll for the whole component if needed
        border: "1px solid #e0e0e0",
        borderRadius: 1,
        minHeight: "400px", // Reserve minimum vertical space
      }}
    >
      <TableContainer
        component={Paper}
        sx={{
          flex: 1,
          overflowX: "auto", // Enables horizontal scrollbar when content exceeds width
          overflowY: "auto",
        }}
      >
        {/* 2. Use size="small" for tighter vertical spacing */}
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {columnOrder.map(
                (col) =>
                  visibleColumns.has(col) && (
                    <TableCell
                      key={col}
                      sx={{
                        // 3. Consistent, smaller minimum width
                        minWidth: 100,
                        // 4. Custom, tighter padding
                        padding: "8px 16px",
                      }}
                    >
                      <Tooltip title={getColumnTooltip(col)}>
                        <TableSortLabel
                          active={sortConfig.column === col}
                          direction={
                            sortConfig.column === col
                              ? sortConfig.direction
                              : "asc"
                          }
                          onClick={() => onSort(col)}
                        >
                          {formatColumnName(col)}
                        </TableSortLabel>
                      </Tooltip>
                    </TableCell>
                  )
              )}
              <TableCell
                sx={{
                  minWidth: 80, // Smaller min width for Action column
                  padding: "8px 16px", // Custom, tighter padding
                }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row, idx) => {
              const isSummaryRow = row.is_summary === true;
              return (
                <TableRow
                  key={row.id || `summary-${idx}`}
                  hover={isSummaryRow}
                  sx={{
                    "&.MuiTableRow-root:hover": isSummaryRow
                      ? {
                          cursor: "pointer",
                          backgroundColor: "#f5f5f5",
                        }
                      : {},
                  }}
                  onClick={() => isSummaryRow && onRowClick(row)}
                >
                  {columnOrder.map(
                    (col) =>
                      visibleColumns.has(col) && (
                        <TableCell
                          key={col}
                          // 5. Custom, tighter padding on body cells
                          sx={{ padding: "8px 16px" }}
                        >
                          {formatCellValue(row[col], col)}
                        </TableCell>
                      )
                  )}
                  <TableCell
                    // 6. Custom, tighter padding on Action cell
                    sx={{ padding: "8px 16px" }}
                  >
                    <Tooltip title={isSummaryRow ? "Details" : "View Entry"}>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          onRowClick(row);
                        }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={data.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => onPageChange(newPage)}
        onRowsPerPageChange={(e) => {
          onRowsPerPageChange(parseInt(e.target.value, 10));
          onPageChange(0);
        }}
      />
    </Box>
  );
};

export default ReportsTableView;
